/**
 * UserDetailsForm Component
 * Extended user details collection form
 */

import React, { useCallback, useState } from 'react';

import { POButton, POButtonType } from '@prizeout/prizeout-ui';
import { SubmitHandler, useForm } from 'react-hook-form';

import { Alert } from '../../components/alert/alert';
import { Checkbox } from '../../components/checkbox/checkbox';
import { FormInput } from '../../components/form-input/form-input';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { selectUserLoading, updateUserDetails } from '../../store/slices';
import { stripPhoneNumber, validationRules } from '../../utils/validation';

import './user-details-form.scss';

export interface UserDetailsInputs {
    firstName: string;
    lastName: string;
    email: string;
    zipCode: string;
    phoneNumber: string;
    acceptTerms: boolean;
}

export interface UserDetailsFormProps {
    onSuccess?: () => void;
}

export const UserDetailsForm: React.FC<UserDetailsFormProps> = ({ onSuccess }) => {
    const dispatch = useAppDispatch();
    const isLoading = useAppSelector(selectUserLoading);
    const [error, setError] = useState<string | null>(null);

    const {
        register,
        handleSubmit,
        formState: { errors, isValid },
        watch,
    } = useForm<UserDetailsInputs>({
        mode: 'onChange',
    });

    const watchAcceptTerms = watch('acceptTerms');

    const onSubmit: SubmitHandler<UserDetailsInputs> = useCallback(
        async (data) => {
            setError(null);

            const trimmed = {
                ...data,
                firstName: data.firstName.trim(),
                lastName: data.lastName.trim(),
                email: data.email.trim(),
                zipCode: data.zipCode.trim(),
                phoneNumber: stripPhoneNumber((data.phoneNumber || '').trim()),
            };

            try {
                await dispatch(updateUserDetails(trimmed)).unwrap();

                onSuccess?.();
            } catch (err) {
                setError(typeof err === 'string' ? err : 'Failed to update details. Please try again.');
            }
        },
        [dispatch, onSuccess],
    );

    return (
        <div className="user-details-form">
            <div className="user-details-form__header">
                <h2 className="user-details-form__title">Welcome to Prizeout.</h2>
                <p className="user-details-form__subtitle">
                    We&apos;ll need some more information before you can choose your gift card.
                </p>
            </div>

            {error && (
                <div className="user-details-form__error">
                    <Alert type="error" message={error} />
                </div>
            )}

            <form
                id="user-details-form"
                className="user-details-form__form"
                onSubmit={handleSubmit(onSubmit)}
                autoComplete="off"
            >
                <FormInput
                    id="firstName"
                    label="First Name*"
                    type="text"
                    placeholder="Enter your first name"
                    error={!!errors.firstName}
                    errorMessage={errors.firstName?.message}
                    {...register('firstName', validationRules.firstName)}
                />

                <FormInput
                    id="lastName"
                    label="Last Name*"
                    type="text"
                    placeholder="Enter your last name"
                    error={!!errors.lastName}
                    errorMessage={errors.lastName?.message}
                    {...register('lastName', validationRules.lastName)}
                />

                <FormInput
                    id="email"
                    label="Email Address* (Gift card will be sent here)"
                    type="email"
                    placeholder="Enter your email address"
                    error={!!errors.email}
                    errorMessage={errors.email?.message}
                    {...register('email', validationRules.email)}
                />

                <FormInput
                    id="zipCode"
                    label="Zip Code*"
                    type="text"
                    placeholder="Enter your zip code"
                    error={!!errors.zipCode}
                    errorMessage={errors.zipCode?.message}
                    {...register('zipCode', validationRules.zipCode)}
                />

                <FormInput
                    id="phoneNumber"
                    label="Phone Number"
                    type="tel"
                    placeholder="(000) 000-0000"
                    maxLength={14}
                    error={!!errors.phoneNumber}
                    errorMessage={errors.phoneNumber?.message}
                    {...register('phoneNumber', validationRules.phoneNumber)}
                />

                <div className="user-details-form__terms">
                    <Checkbox
                        id="acceptTerms"
                        error={!!errors.acceptTerms}
                        label={
                            <>
                                Accept{' '}
                                <a href="https://prizeout.com/terms/" target="_blank" rel="noopener noreferrer">
                                    <strong>Terms & Conditions</strong>
                                </a>{' '}
                                and{' '}
                                <a href="https://prizeout.com/privacy/" target="_blank" rel="noopener noreferrer">
                                    <strong>Privacy Policy</strong>
                                </a>
                            </>
                        }
                        {...register('acceptTerms', { required: true })}
                    />
                </div>
            </form>

            <div className="user-details-form__actions">
                <POButton
                    label="Choose My Gift Card"
                    isLoading={isLoading}
                    isDisabled={!isValid || !watchAcceptTerms}
                    formId="user-details-form"
                    type={POButtonType.SUBMIT}
                />
            </div>
        </div>
    );
};

export default UserDetailsForm;
